// Survey UI app
//
//  Copyright (c) 2025 MX8 Inc
//  All rights reserved.
//
//  This software is confidential and proprietary information of MX8.
//  You shall not disclose such Confidential Information and shall use it only
//  in accordance with the terms of the agreement you entered into with MX8.

import { FC } from "react";
import { Box } from "@mui/material";
import { landscapeImageStyle, questionContentContainer } from "./styles";
import CommonQuestionErrorComp from "../CommonQuestionErrorComp";
import CommonImageComp from "../CommonImageComp";
import { useThemeContext } from "../../../globalContext/global-context";

type QuestionContentFrameProps = {
  content: string;
  error?: string;
  image?: string;
  altText?: string;
  orientation?: string;
  imageMaxHeight: number | string;
  fillAvailableImageSpace?: boolean;
};

export const QuestionContentFrame: FC<QuestionContentFrameProps> = ({
  content,
  error,
  image,
  altText,
  orientation,
  imageMaxHeight,
  fillAvailableImageSpace = false,
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const isImagePresent = !!(image && image.length > 0);

  return (
    <Box
      data-testid="question-content"
      sx={{
        ...questionContentContainer(orientation, theme),
        ...(fillAvailableImageSpace
          ? {
              justifyContent: "flex-start",
              minHeight: 0,
              overflowY: "auto",
            }
          : {}),
      }}
    >
      <CommonQuestionErrorComp questionText={content} errorText={error} />

      {isImagePresent && (
        <Box
          data-testid="question-image-frame"
          sx={
            fillAvailableImageSpace
              ? {
                  flex: "1 1 auto",
                  minHeight: `${theme.tokens.spacing["6"] * 4}px`,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }
              : undefined
          }
        >
          <CommonImageComp
            currentVal={{
              image,
              alt_text: altText || theme.copy.labels.landscapeImage,
            }}
            imageTestid="landscape-content-image"
            imageStyle={landscapeImageStyle(theme, imageMaxHeight)}
            fillContainer={fillAvailableImageSpace}
          />
        </Box>
      )}
    </Box>
  );
};
